import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';


// Section definition: each section has an id, icon, and bilingual fields
const SECTIONS = [
  {
    id: 'general',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
      </svg>
    ),
    labelKey: 'cms.general_description',
    labelDefault: 'General Description',
    fields: [
      { name: 'description', type: 'textarea', placeholderEn: 'Describe this breed...', placeholderAr: 'وصف هذا النوع...' }
    ]
  },
  {
    id: 'temperament',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
    ),
    labelKey: 'cms.temperament',
    labelDefault: 'Temperament',
    fields: [
      { name: 'temperament', type: 'textarea', placeholderEn: 'e.g. Loyal, Intelligent, Energetic', placeholderAr: 'مثال: مخلص، ذكي، نشيط' }
    ]
  },
  {
    id: 'appearance',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"></circle>
        <circle cx="12" cy="10" r="3"></circle>
        <path d="M7 20.662V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.662"></path>
      </svg>
    ),
    labelKey: 'cms.appearance',
    labelDefault: 'Appearance',
    fields: [
      { name: 'appearance', type: 'textarea', placeholderEn: 'Describe coat, size, build...', placeholderAr: 'وصف الشكل الخارجي...' }
    ]
  },
  {
    id: 'lifespan',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
    ),
    labelKey: 'cms.lifespan',
    labelDefault: 'Life Span',
    fields: [
      { name: 'lifespan', type: 'input', placeholderEn: 'e.g. 10-14 years', placeholderAr: 'مثال: 10-14 سنة' }
    ]
  },
  {
    id: 'grooming',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      </svg>
    ),
    labelKey: 'cms.grooming',
    labelDefault: 'Grooming & Shedding',
    fields: [
      { name: 'grooming', type: 'textarea', placeholderEn: 'Describe grooming needs and shedding level...', placeholderAr: 'وصف احتياجات العناية والتساقط...' }
    ]
  },
  {
    id: 'health',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
      </svg>
    ),
    labelKey: 'cms.health',
    labelDefault: 'Health',
    fields: [
      { name: 'health', type: 'textarea', placeholderEn: 'Common health issues and lifespan considerations...', placeholderAr: 'المشاكل الصحية الشائعة...' }
    ]
  },
  {
    id: 'nutrition',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
        <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
        <line x1="6" y1="1" x2="6" y2="4"></line>
        <line x1="10" y1="1" x2="10" y2="4"></line>
        <line x1="14" y1="1" x2="14" y2="4"></line>
      </svg>
    ),
    labelKey: 'cms.nutrition',
    labelDefault: 'Nutrition',
    fields: [
      { name: 'nutrition', type: 'textarea', placeholderEn: 'Dietary needs and feeding recommendations...', placeholderAr: 'الاحتياجات الغذائية وتوصيات التغذية...' }
    ]
  },
  {
    id: 'facts',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <circle cx="12" cy="16" r="0.8" fill="currentColor" stroke="none" />
      </svg>
    ),
    labelKey: 'cms.facts',
    labelDefault: 'Facts',
    fields: [
      { name: 'facts', type: 'textarea', placeholderEn: 'Interesting facts about this breed...', placeholderAr: 'حقائق مثيرة للاهتمام حول هذا النوع...' }
    ]
  }
];

const BreedForm = ({
  formData,
  handleInputChange,
  handleSubmit,
  handleCancel,
  submitting,
  isEditing,
  species,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [imagePreview, setImagePreview] = useState(null);

  const [openSections, setOpenSections] = useState(() =>
    SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );

  // Update preview when file is selected or existing image is present
  useEffect(() => {
    if (formData.image && formData.image instanceof File) {
      const url = URL.createObjectURL(formData.image);
      setImagePreview(url);
      return () => URL.revokeObjectURL(url);
    } else if (formData.existing_image_url) {
      setImagePreview(formData.existing_image_url);
    } else if (formData.image_url) {
      setImagePreview(formData.image_url);
    } else {
      setImagePreview(null);
    }
  }, [formData.image, formData.existing_image_url, formData.image_url]);

  const toggleSection = (id) =>
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));

  const handleImageUrlBlur = () => {
    if (formData.image_url) setImagePreview(formData.image_url);
  };

  const clearImage = () => {
    setImagePreview(null);
    handleInputChange({ target: { name: 'image_url', value: '' } });
    handleInputChange({ target: { name: 'image', value: null, type: 'file', files: [] } });
    if (isEditing) {
      handleInputChange({ target: { name: 'remove_image', value: true, type: 'checkbox', checked: true } });
      handleInputChange({ target: { name: 'existing_image_url', value: '' } });
    }
  };

  return (
    <div className={`breed-form-view ${isRTL ? 'rtl' : ''}`}>

      {/* ── Page Header ── */}
      <div className="form-header justify-between">
        <div className="header-left">
          <button className="back-btn" onClick={handleCancel}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            {t('adminDashboard.cms.back_to_list', 'Back to List')}
          </button>
          <h3>{isEditing ? t('adminDashboard.cms.edit_breed', 'Edit Breed') : t('adminDashboard.cms.add_breed_data', 'Add Breed Data')}</h3>

        </div>
        <div className="status-field">
          <div className={`active-switch-row ${!formData.species_id ? 'disabled' : ''}`}>
            <label 
              htmlFor="is_active_toggle" 
              className={`switch-label ${!formData.is_active ? 'switch-label--active' : ''}`}
            >
              {t('adminDashboard.cms.inactive', 'Inactive')}
            </label>
            <label className="status-toggle" htmlFor="is_active_toggle">
              <input
                type="checkbox"
                id="is_active_toggle"
                name="is_active"
                checked={!!formData.is_active}
                onChange={handleInputChange}
                disabled={!formData.species_id}
              />
              <span className="status-toggle-track"></span>
            </label>
            <label 
              htmlFor="is_active_toggle" 
              className={`switch-label ${formData.is_active ? 'switch-label--active' : ''}`}
            >
              {t('adminDashboard.cms.active', 'Active')}
            </label>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="breed-form-content">

        {/* ── Meta Section 1: Species & Breed ── */}
        <div className="bfc-meta-row bfc-section">
          <div className="form-group">
            <label>{t('adminDashboard.cms.species', 'Species')} <span className="required">*</span></label>
            <select name="species_id" value={formData.species_id} onChange={handleInputChange} required>
              <option value="">{t('adminDashboard.cms.select_species', 'Select Species')}</option>
              {Array.isArray(species) && species.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name || s.display_name || s.name_en || s.id}
                </option>
              ))}
            </select>
          </div>

        </div>

        {/* ── Meta Section 2: Bilingual Names ── */}
        <div className="bfc-meta-row bfc-section">
          <div className="form-group bfc-name-group">
            <label>{t('adminDashboard.cms.name_en', 'Name (EN)')} <span className="required">*</span></label>
            <input 
              type="text" 
              name="name_en" 
              value={formData.name_en} 
              onChange={handleInputChange} 
              required 
              placeholder="e.g. German Shepherd" 
              disabled={!formData.species_id}
            />
          </div>

          <div className="form-group bfc-name-group" dir="rtl">
            <label>{t('adminDashboard.cms.name_ar', 'الاسم (AR)')} <span className="required">*</span></label>
            <input 
              type="text" 
              name="name_ar" 
              value={formData.name_ar} 
              onChange={handleInputChange} 
              required 
              placeholder="مثال: الراعي الألماني" 
              disabled={!formData.species_id}
            />
          </div>
        </div>



        {/* ── Bilingual Content Sections ── */}
        {SECTIONS.map(section => (
          <div key={section.id} className="bfc-section">
            <button
              type="button"
              className="bfc-section-header"
              onClick={() => toggleSection(section.id)}
            >
              <span className="bfc-section-icon">{section.icon}</span>
              <span className="bfc-section-title">{t(section.labelKey, section.labelDefault)}</span>
              <svg
                className={`bfc-chevron ${openSections[section.id] ? 'open' : ''}`}
                width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {openSections[section.id] && (
              <div className="bfc-section-body">
                {section.fields.map(field => (
                  <div key={field.name} className="bfc-lang-grid">
                    {/* English */}
                    <div className="form-group">
                      <label className="lang-label">
                        <span className="lang-badge en">EN</span>
                        {t(`cms.${field.name}_en`, field.name.charAt(0).toUpperCase() + field.name.slice(1))}
                      </label>
                      {field.type === 'textarea' ? (
                          <textarea
                            name={`${field.name}_en`}
                            value={formData[`${field.name}_en`] || ''}
                            onChange={handleInputChange}
                            rows="4"
                            placeholder={field.placeholderEn}
                            disabled={!formData.species_id}
                          />
                        ) : (
                          <input
                            type="text"
                            name={`${field.name}_en`}
                            value={formData[`${field.name}_en`] || ''}
                            onChange={handleInputChange}
                            placeholder={field.placeholderEn}
                            disabled={!formData.species_id}
                          />
                      )}
                    </div>

                    {/* Arabic */}
                    <div className="form-group" dir="rtl">
                      <label className="lang-label">
                        <span className="lang-badge ar">AR</span>
                        {t(`cms.${field.name}_ar`, field.name.charAt(0).toUpperCase() + field.name.slice(1))}
                      </label>
                      {field.type === 'textarea' ? (
                        <textarea
                          name={`${field.name}_ar`}
                          value={formData[`${field.name}_ar`] || ''}
                          onChange={handleInputChange}
                          rows="4"
                          dir="rtl"
                          placeholder={field.placeholderAr}
                          disabled={!formData.species_id}
                        />
                      ) : (
                        <input
                          type="text"
                          name={`${field.name}_ar`}
                          value={formData[`${field.name}_ar`] || ''}
                          onChange={handleInputChange}
                          dir="rtl"
                          placeholder={field.placeholderAr}
                          disabled={!formData.species_id}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* ── Image Section ── */}
        <div className="bfc-section">
          <button type="button" className="bfc-section-header" onClick={() => toggleSection('image')}>
            <span className="bfc-section-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </span>
            <span className="bfc-section-title">{t('adminDashboard.cms.image', 'Picture of the Breed')}</span>
            <svg
              className={`bfc-chevron ${openSections['image'] ? 'open' : ''}`}
              width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>

          {openSections['image'] && (
            <div className="bfc-section-body">
              <div className="image-section">
                {/* Inputs */}
                <div className="image-inputs">
                  <div className="form-group">
                    <label>{t('adminDashboard.cms.image_url', 'Static Image URL')}</label>
                    <input
                      type="text"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleInputChange}
                      onBlur={handleImageUrlBlur}
                      placeholder="https://example.com/image.jpg"
                      disabled={!formData.species_id}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('adminDashboard.cms.upload_image', 'Upload Image')}</label>
                    <div className="file-input-wrapper">
                        <input
                          type="file"
                          name="image"
                          onChange={handleInputChange}
                          accept="image/*"
                          id="upload-image"
                          style={{ display: 'none' }}
                          disabled={!formData.species_id}
                        />
                        <label htmlFor="upload-image" className={`file-label ${!formData.species_id ? 'disabled' : ''}`}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        {formData.image ? formData.image.name : t('adminDashboard.cms.choose_file', 'Choose file')}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="image-preview-panel">
                  <span className="image-preview-label">{t('adminDashboard.cms.image_preview', 'Image Preview')}</span>
                  {imagePreview ? (
                    <div className="image-preview-box">
                      <img src={imagePreview} alt="Preview" className="image-preview-img" onError={() => setImagePreview(null)} />
                      <button 
                        type="button" 
                        className="image-preview-clear" 
                        onClick={clearImage} 
                        title="Remove image"
                        disabled={!formData.species_id}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="image-preview-empty">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>{t('adminDashboard.cms.no_image', 'No image selected')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer Actions ── */}
        <div className="form-footer">
          <button type="button" className="cancel-btn-large" onClick={handleCancel}>
            {t('cancel')}
          </button>
          <button type="submit" className="submit-btn-large" disabled={submitting || !formData.species_id || !formData.name_en?.trim() || !formData.name_ar?.trim()}>
            {submitting ? t('saving') : (isEditing ? t('update') : t('create'))}
          </button>
        </div>

      </form>
    </div>
  );
};

export default BreedForm;
